import { useState, useEffect } from "react";
import api, { formatApiError } from "@/utils/api";
import { fmt, fmtDateTime } from "@/utils/format";
import { AYARLAR } from "@/constants/testIds";
import { toast } from "sonner";
import { RefreshCw, Save, Settings2 } from "lucide-react";
import { playOrderNotification } from "@/utils/orderSound";
import { useAuth } from "@/contexts/AuthContext";
import BrandLogoManager from "@/components/BrandLogoManager";
import { useSearchParams } from "react-router-dom";

export default function Ayarlar() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const logoBrand = searchParams.get("logoMarka") || "";
  const [eurKuru, setEurKuru] = useState(null);
  const [manuelKur, setManuelKur] = useState("");
  const [fiyatListeleri, setFiyatListeleri] = useState([]);
  const [loadingTcmb, setLoadingTcmb] = useState(false);
  const [savingKur, setSavingKur] = useState(false);
  const [editFl, setEditFl] = useState({});
  const [orderSound, setOrderSound] = useState(() => localStorage.getItem("vagenpar_order_sound") !== "off");
  const [orderVolume, setOrderVolume] = useState(() => Number(localStorage.getItem("vagenpar_order_volume") || 0.75));

  const testOrderSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      playOrderNotification(ctx, orderVolume);
      localStorage.setItem("vagenpar_order_sound", "on");
      setOrderSound(true);
      toast.success("Bildirim sesi etkinleştirildi");
    } catch { toast.error("Tarayıcı bildirim sesini başlatamadı"); }
  };

  useEffect(() => {
    api.get("/settings/eur-kuru").then((r) => { setEurKuru(r.data); setManuelKur(r.data.kur?.toFixed(4) || ""); }).catch(() => {});
    api.get("/fiyat-listeleri/").then((r) => setFiyatListeleri(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!logoBrand) return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("marka-logolari")?.scrollIntoView({ block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [logoBrand]);

  const handleTcmbFetch = async () => {
    setLoadingTcmb(true);
    try {
      const r = await api.post("/settings/eur-kuru/tcmb");
      setEurKuru(r.data);
      setManuelKur(r.data.kur?.toFixed(4) || "");
      toast.success(`TCMB Efektif Satış kuru alındı: ${r.data.kur}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingTcmb(false);
    }
  };

  const handleSaveKur = async (e) => {
    e.preventDefault();
    setSavingKur(true);
    try {
      const kur = parseFloat(manuelKur);
      if (!kur) throw new Error("Geçerli bir kur giriniz");
      const r = await api.put("/settings/eur-kuru", { kur, kaynak: "manuel" });
      setEurKuru(r.data);
      toast.success("EUR kuru güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingKur(false);
    }
  };

  const handleSaveFl = async (fl) => {
    try {
      const updates = editFl[fl.id] || {};
      const r = await api.put(`/fiyat-listeleri/${fl.id}`, updates);
      setFiyatListeleri((prev) => prev.map((f) => (f.id === fl.id ? r.data : f)));
      setEditFl((p) => { const n = { ...p }; delete n[fl.id]; return n; });
      toast.success("Fiyat listesi güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0D1B3E]" style={{ fontFamily: "Outfit, sans-serif" }}>Ayarlar</h1>
        <p className="text-sm text-slate-500 mt-0.5">Sistem ayarları ve yapılandırma</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between gap-4"><div><h2 className="text-base font-semibold text-slate-800">Yeni sipariş bildirim sesi</h2><p className="text-sm text-slate-500 mt-1">Yeni ve okunmamış B2B siparişinde daha güçlü ve uzun, dört tonlu bildirim çalar.</p></div><button type="button" onClick={() => { const next = !orderSound; setOrderSound(next); localStorage.setItem("vagenpar_order_sound", next ? "on" : "off"); }} className={`w-12 h-7 rounded-full p-1 transition-colors ${orderSound ? "bg-green-500" : "bg-slate-300"}`}><span className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${orderSound ? "translate-x-5" : ""}`} /></button></div>
        <div className="mt-4 flex items-center gap-3"><span className="text-xs font-semibold text-slate-500">Ses seviyesi</span><input type="range" min="0.1" max="1" step="0.05" value={orderVolume} onChange={(e) => { const value = Number(e.target.value); setOrderVolume(value); localStorage.setItem("vagenpar_order_volume", String(value)); }} className="w-44"/><button type="button" onClick={testOrderSound} className="text-xs border border-[#0D1B3E] text-[#0D1B3E] px-3 py-1.5 rounded-md hover:bg-slate-50">Sesi dene ve etkinleştir</button></div>
      </div>

      {/* EUR Rate */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-5">
          <Settings2 size={18} className="text-[#0D1B3E]" />
          <h2 className="text-base font-semibold text-slate-800">EUR / TRY Kuru</h2>
        </div>

        {eurKuru && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Güncel Kur</div>
                <div className="text-3xl font-bold text-[#0D1B3E] tabular-nums">{fmt(eurKuru.kur, 4)}</div>
                <div className="text-xs text-slate-400 mt-1">
                  Kaynak: <span className="font-semibold capitalize">{eurKuru.kaynak}</span>{eurKuru.kur_tipi === "efektif_satis" ? " · Efektif Satış" : ""} · {fmtDateTime(eurKuru.tarih)}
                </div>
              </div>
              <button
                data-testid={AYARLAR.tcmbFetch}
                onClick={handleTcmbFetch}
                disabled={loadingTcmb}
                className="flex items-center gap-2 bg-[#0D1B3E] text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#172A58] disabled:opacity-60 transition-all"
              >
                <RefreshCw size={14} className={loadingTcmb ? "animate-spin" : ""} />
                {loadingTcmb ? "Çekiliyor..." : "TCMB'den Güncelle"}
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSaveKur} className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Manuel Kur Girişi</label>
            <input
              data-testid={AYARLAR.eurInput}
              type="number"
              step="0.0001"
              value={manuelKur}
              onChange={(e) => setManuelKur(e.target.value)}
              placeholder="38.5000"
              className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20 focus:border-[#0D1B3E] transition-colors tabular-nums"
            />
          </div>
          <button
            data-testid={AYARLAR.eurSave}
            type="submit"
            disabled={savingKur}
            className="flex items-center gap-2 bg-slate-700 text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60 transition-colors"
          >
            <Save size={14} />
            {savingKur ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </form>
      </div>

      {/* Price Lists */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <h2 className="text-base font-semibold text-slate-800 mb-5">Fiyat Listeleri</h2>
        <div className="space-y-3">
          {fiyatListeleri.map((fl) => {
            const isEditing = !!editFl[fl.id];
            const curr = editFl[fl.id] || fl;
            return (
              <div key={fl.id} className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex-1">
                  <div className="font-semibold text-slate-800">{fl.ad}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {fl.ad} fiyat listesi (%{((fl.oran || 0) * 100).toFixed(2).replace(/[,\.]?0+$/, "")} markup)
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isEditing ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Oran %</span>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          max="100"
                          value={Number(((curr.oran || 0) * 100).toFixed(2))}
                          onChange={(e) => setEditFl((p) => ({ ...p, [fl.id]: { ...curr, oran: parseFloat(e.target.value) / 100 } }))}
                          onKeyDown={(e) => {
                            if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                            e.preventDefault();
                            const currentPercentage = Number(((curr.oran || 0) * 100).toFixed(2));
                            const nextPercentage = Math.min(100, Math.max(0, currentPercentage + (e.key === "ArrowUp" ? 1 : -1)));
                            setEditFl((p) => ({ ...p, [fl.id]: { ...curr, oran: nextPercentage / 100 } }));
                          }}
                          className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-[#0D1B3E]/20"
                        />
                      </div>
                      <button onClick={() => handleSaveFl(fl)} className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 transition-colors">Kaydet</button>
                      <button onClick={() => setEditFl((p) => { const n = {...p}; delete n[fl.id]; return n; })} className="text-xs text-slate-500 hover:text-slate-700 transition-colors">İptal</button>
                    </>
                  ) : (
                    <>
                      <div className="text-right">
                        <div className="text-lg font-bold text-[#0D1B3E] tabular-nums">%{((fl.oran || 0) * 100).toFixed(0)}</div>
                        <div className="text-xs text-slate-400">iskonto</div>
                      </div>
                      <button onClick={() => setEditFl((p) => ({ ...p, [fl.id]: { ...fl } }))}
                        className="text-xs border border-slate-300 text-slate-600 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors">Düzenle</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-4">
          Fiyat formülü: <strong>Liste Fiyatı × (1 − İskonto) × 1.20 KDV</strong><br/>
          VW ORİJİNAL / PORSCHE ORİJİNAL markalarda iskonto uygulanmaz; yalnızca %20 KDV eklenir.
        </p>
      </div>

      {user?.role === "admin" && <BrandLogoManager initialBrand={logoBrand} />}
    </div>
  );
}
